import { setTag } from '@sentry/react';
import { getLocale } from 'i18n/i18nUtils';
import { supportsWebp } from 'rangeConfig/shared/utils/optimizedModels';
import { useEffect } from 'react';
import { getKioskId, getPlatform, isKiosk } from 'utils/platform';
import { getRange } from 'utils/queryUtils';
import packageInfo from '../../package.json';
import { isSpaceEnabled } from 'utils/integration';

const cleanVersion = (version: string) => {
  // Taken from skytta
  // This regex will remove any character that is not a digit (0-9) or a period '.'
  // Version numbers are usually specified for dependencies using certain characters like or other range specifiers.
  // Use this when you want to parse these version numbers and remove these characters to get the clean version number.
  return version.replace(/[^0-9.]/g, '');
};

export const sentryTags = {
  release: 'release',
  range: 'range',
  platform: 'platform',
  uiPlatform: 'uiPlatform',
  locale: 'locale',
  webpSupport: 'webpSupport',
  computerId: 'computerId',
  coreVersion: 'coreVersion',
  betraktaVersion: 'betraktaVersion',
  kompisVersion: 'kompisVersion',
  insightsVersion: 'insightsVersion',
  kvadratVersion: 'kvadratVersion',
  spaceEnabled: 'spaceEnabled',
  userLoggedIn: 'userLoggedIn'
};

// ToDo, set insights tags if it's initialized
const useSentryTags = () => {
  const range = getRange();
  const platform = getPlatform();
  const uiPlatform = isKiosk() ? 'kiosk' : 'web';
  const locale = getLocale();
  const computerId = getKioskId();
  const spaceEnabled = isSpaceEnabled();

  const getWebpSupport = async () => {
    const webpSuppoerted = await supportsWebp();

    setTag(sentryTags.webpSupport, webpSuppoerted);
  };

  useEffect(() => {
    setTag(sentryTags.range, range);
    setTag(sentryTags.platform, platform);
    setTag(sentryTags.uiPlatform, uiPlatform);
    setTag(sentryTags.locale, locale);
    setTag(sentryTags.computerId, computerId);
    setTag(sentryTags.coreVersion, cleanVersion(packageInfo.dependencies['@inter-ikea-core/core']));
    setTag(
      sentryTags.betraktaVersion,
      cleanVersion(packageInfo.dependencies['@inter-ikea-betrakta/viewer'])
    );
    setTag(
      sentryTags.kompisVersion,
      cleanVersion(packageInfo.dependencies['@inter-ikea-kompis/react-components'])
    );
    setTag(
      sentryTags.insightsVersion,
      cleanVersion(packageInfo.dependencies['@insights/insights-data-provider'])
    );
    setTag(
      sentryTags.kvadratVersion,
      cleanVersion(packageInfo.dependencies['@inter-ikea-kvadrat/kvadrat'])
    );
    setTag(sentryTags.spaceEnabled, spaceEnabled);

    getWebpSupport();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useSentryTags;
